<script setup>
import { computed } from 'vue';
import { useBlocks } from '@/composables/useBlocks';
import { useSeo } from '@/composables/useSeo';
import {
  useCatalogFilters,
  getCatalogTypeFromPath,
} from '@/composables/useCatalogFilters';
import {
  fetchCatalogPayload,
  getCatalogCachedData,
} from '@/composables/useCatalogData';
import BlockSeoText from '@/components/blocks/BlockSeoText.vue';
import { useGlobalStore } from '@/stores/global';
const props = defineProps({
  data: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  isStock: {
    type: Boolean,
    default: false,
  },
  isCatalog: {
    type: Boolean,
    default: false,
  },
});

const globalStore = useGlobalStore();
const blockComponents = useBlocks();
const sections = computed(() => {
  return props.data?.sections || [];
});

// SSR priming: sections like BlockCarsNew/BlockPromosSlider read
// globalStore.catalogPageTitle for their hero <h1>, but they render as
// EARLIER siblings of the catalog block in the v-for below. BlockCatalog
// only writes that store field once its own async fetch resolves, and
// Vue's SSR renderer serializes siblings in document order without
// waiting for a later sibling's async work — so the hero block always
// serialized before that value existed. Fetching (and awaiting) the same
// catalog payload here, before this component renders any section, fixes
// that ordering. The key matches what BlockCatalog's own useCatalogData
// call uses (`catalog-${type}:${apiUrl}`), so Nuxt dedupes this against
// that later call instead of firing a second network request.
//
// That dedupe needs `getCatalogCachedData` passed here explicitly (not
// just from useCatalogData's own call): nuxtApp._asyncData[key] — and the
// execute()/options closure attached to it — is created once by whichever
// of the two `useAsyncData` calls for this key runs first, and reused
// as-is by the other. Since this priming call always runs first (awaited
// here, in the parent, before BlockCatalog even mounts), it's the one
// whose options actually end up governing every later execute() against
// this key, including BlockCatalog's — passing the option only on
// BlockCatalog's side (the second call) would silently have no effect.
if (props.isCatalog) {
  const route = useRoute();
  const catalogType = getCatalogTypeFromPath(route.path);
  if (catalogType) {
    const { apiUrl } = useCatalogFilters(catalogType);
    const { data: primedCatalog } = await useAsyncData(
      () => `catalog-${catalogType}:${apiUrl.value}`,
      () => fetchCatalogPayload(apiUrl.value),
      { default: () => null, getCachedData: getCatalogCachedData },
    );
    const primedTitle = primedCatalog.value?.meta?.h1;
    const primedDescription = primedCatalog.value?.meta?.description;
    if (primedTitle) globalStore.setCatalogPageTitle(primedTitle);
    if (primedDescription)
      globalStore.setCatalogPageMetaDescription(primedDescription);
  }
}

useSeo({
  title: computed(() => props.data?.seo_title || props.data?.title || ''),
  description: computed(() => props.data?.meta_description || ''),
  keywords: computed(() => props.data?.meta_keywords || ''),
  robots: computed(() => props.data?.meta_robots || 'index, follow'),
  image: computed(() => props.data?.image_cover?.src || null),
  path: computed(() => props.data?.url || ''),
});
</script>

<template>
  <div class="page-dynamic">
    <Breadcrumbs
      v-if="data?.breadcrumbs?.items"
      :breadcrumbs="data.breadcrumbs.items"
    />
    <template
      v-for="(block, index) in sections"
      :key="block.id + '-' + $route.fullPath"
    >
      <component
        :is="blockComponents[block.type]"
        :id="block.id"
        :data="block.data"
        :is-first-section="index === 0"
        :page-type="data?.slug"
        :path="data?.slug"
        :title="
          block.type === 'catalog'
            ? data?.seo_title
              ? data?.seo_title
              : data?.title
            : ''
        "
        :description="block.type === 'catalog' ? data?.meta_description : ''"
        :keywords="block.type === 'catalog' ? data?.meta_keywords : ''"
        :robots="block.type === 'catalog' ? data?.meta_robots : ''"
        :url="block.type === 'catalog' ? data?.url : ''"
        :image="
          block.type === 'catalog'
            ? (data?.image_cover && data?.image_cover?.src) || null
            : ''
        "
        :is-stock="isStock"
        :is-catalog="isCatalog"
      />
    </template>
    <BlockSeoText
      v-if="globalStore?.catalogPageMetaDescription"
      :data="{
        background_color: 'gray',
        content: globalStore?.catalogPageMetaDescription,
        spacing: {
          top: 'sm',
          bottom: 'xxl',
        },
      }"
    />
  </div>
</template>
